'use client';

import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { e2eMockUserEmail } from './e2eMockUser';
import { readCreateOwnerEmail } from '@/platform/saas/createOwnerEmailCookie';
import {
  OPS_AUTH_CLEARED_EVENT,
  consumeHashIdToken,
  emailFromIdToken,
  readOpsIdToken,
  readOpsUid,
} from './idTokenStore';

function opsBounceUser(): User | null {
  const storedUid = readOpsUid();
  if (!storedUid) return null;
  const storedToken = readOpsIdToken();
  return {
    uid: storedUid,
    displayName: null,
    email: storedToken ? emailFromIdToken(storedToken) : null,
    providerData: [],
    getIdToken: async () => readOpsIdToken() || '',
  } as unknown as User;
}

/** Prefer Firebase user; otherwise ops bounce session (slug ← apex login). */
export function resolveClientAuthUser(firebaseUser: User | null): User | null {
  if (firebaseUser) return firebaseUser;
  return opsBounceUser();
}

/** Subscribes to Firebase auth state for client components. */
export function useFirebaseUser(): User | null {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const mockUid = process.env.NEXT_PUBLIC_E2E_MOCK_UID;
    if (mockUid) {
      if (typeof window !== 'undefined' && sessionStorage.getItem('e2e-force-logout') === '1') {
        setUser(null);
        return;
      }
      setUser({
        uid: mockUid,
        displayName: 'E2E User',
        email: e2eMockUserEmail(readCreateOwnerEmail()),
        providerData: [],
      } as unknown as User);
      return;
    }

    // Marketing product tour — no Firebase project on the embed page.
    if (process.env.NEXT_PUBLIC_TOUR_BUILD === 'true') {
      setUser(null);
      return;
    }

    // Hash bounce must run before reading ops session (same tick as AuthProvider).
    consumeHashIdToken();

    function applyResolved(fbUser: User | null) {
      setUser(resolveClientAuthUser(fbUser));
    }

    let unsubAuth: (() => void) | undefined;
    try {
      const auth = getFirebaseAuth();
      unsubAuth = onAuthStateChanged(auth, applyResolved);
    } catch {
      applyResolved(null);
    }

    function onOpsCleared() {
      try {
        const auth = getFirebaseAuth();
        applyResolved(auth.currentUser);
      } catch {
        applyResolved(null);
      }
    }
    window.addEventListener(OPS_AUTH_CLEARED_EVENT, onOpsCleared);

    return () => {
      unsubAuth?.();
      window.removeEventListener(OPS_AUTH_CLEARED_EVENT, onOpsCleared);
    };
  }, []);

  return user;
}
